import React, { useEffect, useRef, useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';

const toastAnimationStyles = `
@keyframes feedbackToastIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes feedbackToastOut {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(12px); }
}
`;

interface FeedbackToastProps {
  show: boolean;
  onDismiss: () => void;
  message?: string;
  variant?: 'success' | 'error';
}

const VARIANT_STYLES = {
  success: {
    border: 'border-green-600',
    background: '#DCFAE8',
    iconBg: 'bg-green-600',
    Icon: CheckCircle,
  },
  error: {
    border: 'border-red-500',
    background: '#FEE2E2',
    iconBg: 'bg-red-500',
    Icon: AlertCircle,
  },
};

const FeedbackToast: React.FC<FeedbackToastProps> = ({
  show,
  onDismiss,
  message = 'Thank you! Your feedback helps make\n3M Navigator better for everyone.',
  variant = 'success',
}) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onDismiss();
    }, 200);
  };

  useEffect(() => {
    if (show) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setExiting(false);
      setVisible(true);
      timerRef.current = setTimeout(dismiss, 5000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, message, variant]);

  if (!visible) return null;

  const { border, background, iconBg, Icon } = VARIANT_STYLES[variant];

  return (
    <>
      <style>{toastAnimationStyles}</style>
      <div className="mb-2 flex justify-end">
        <div
          className={`flex items-center gap-3 rounded-lg border-l-4 ${border} px-4 py-3 shadow-lg`}
          style={{
            backgroundColor: background,
            color: 'black',
            animation: exiting
              ? 'feedbackToastOut 200ms ease-in forwards'
              : 'feedbackToastIn 250ms ease-out',
          }}
        >
          <div className={`flex-shrink-0 w-6 h-6 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon size={18} className="text-white" />
          </div>
          <span className="text-sm font-medium whitespace-pre-line">
            {message}
          </span>
          <button
            onClick={dismiss}
            className="flex-shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors ml-2"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default FeedbackToast;
